import React, { useState, useEffect } from "react";
import { Box, Button, useTheme } from "@mui/material";
import axios from "axios";
import { useColourMode } from "../contexts/ColourContext";
import { ReactComponent as SearchIcon } from "../images/svg/search.svg";
import { capitalizeFirstLetter } from "../functions";
import { prodApi } from "../data/api";

const url = `${prodApi.data}/users`;

const User = ({ adminId, userId, firstName, lastName, email, accessLevel }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({
    accessLevel: accessLevel || "",
    email: email,
    firstName: firstName,
  });

  const colourTheme = useTheme();

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("Authorization token is missing.");
        return;
      }

      await axios.put(
        `https://www.data.banilux.ng/data/users/${adminId}/${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // alert("Data updated successfully!");
      setIsExpanded(false);
      window.location.reload();
    } catch (error) {
      console.error("Error updating data:", error);
      // alert("Failed to update data. Please try again.");
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("Authorization token is missing.");
        return;
      }

      const confirmed = window.confirm(
        "Are you sure you want to delete this user?"
      );
      if (confirmed) {
        await axios.delete(
          `https://www.data.banilux.ng/data/users/${adminId}/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        alert("User deleted successfully!");
        setIsExpanded(false);
      }
      window.location.reload();
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  return (
    <Box onClick={toggleExpand} sx={{ cursor: "pointer", margin: "0.75rem" }}>
      {/* User */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.9rem",
        }}
      >
        <Box>
          {lastName} {firstName}
        </Box>
        <Box>{capitalizeFirstLetter(accessLevel)}</Box>
        {isExpanded && (
          <Button
            variant="outlined"
            color="secondary"
            sx={{
              flex: 0.5,
              fontSize: "0.65rem",
              color: "white",
              backgroundColor: "red",
              "&:hover": { backgroundColor: "darkred" },
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent collapsing the form
              handleDelete();
            }}
          >
            Delete
          </Button>
        )}
      </Box>
      {isExpanded && (
        <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: "column",
              gap: "0.125rem",
              fontSize: "0.9rem",
            }}
          >
            {/* Access Level */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "0.5rem",
              }}
            >
              <label>Access Level:</label>
              <select
                name="accessLevel"
                value={formData.accessLevel}
                onChange={handleInputChange}
                style={{
                  width: "50%",
                  padding: "0.5rem",
                  borderRadius: "5px",
                  fontSize: "0.9rem",
                  background: "transparent",
                  border: `1px solid ${colourTheme.palette.text.primary}`,
                  color: colourTheme.palette.text.primary,
                  appearance: "none",
                }}
              >
                <option
                  value=""
                  disabled
                  style={{
                    background: colourTheme.palette.background.default,
                    color: colourTheme.palette.text.secondary,
                  }}
                >
                  Select
                </option>
                <option
                  value="pending"
                  style={{
                    background: colourTheme.palette.background.default,
                    color: colourTheme.palette.text.primary,
                  }}
                >
                  Pending
                </option>

                <option
                  value="user"
                  style={{
                    background: colourTheme.palette.background.default,
                    color: colourTheme.palette.text.primary,
                  }}
                >
                  User
                </option>

                <option
                  value="data"
                  style={{
                    background: colourTheme.palette.background.default,
                    color: colourTheme.palette.text.primary,
                  }}
                >
                  Data Analyst
                </option>

                <option
                  value="admin"
                  style={{
                    background: colourTheme.palette.background.default,
                    color: colourTheme.palette.text.primary,
                  }}
                >
                  Administrator
                </option>
              </select>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                m: "1.5rem 0",
              }}
            >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ flex: 1, mr: 1, fontSize: "0.65rem" }}
              >
                Submit
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                sx={{ flex: 1, fontSize: "0.65rem" }}
                onClick={toggleExpand}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </Box>
  );
};

const ManageUsers = ({ adminId, header }) => {
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [searchUsers, setSearchUsers] = useState("");
  const [error, setError] = useState("");

  const colourTheme = useTheme();
  const { accentColour } = useColourMode();

  const handleSearchChange = (e) => {
    setSearchUsers(e.target.value);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          setError("Authorization token is missing.");
          return;
        }

        if (usersLoaded) {
          // Data is already loaded; no need to fetch again
          return;
        }

        const response = await axios.get(`${url}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Process and store the users' data
        setUsers(response.data);
        setUsersLoaded(true);
      } catch (error) {
        console.error("Error fetching users data:", error);
        const message =
          error.response && error.response.status === 403
            ? "You do not have the necessary permissions."
            : "Failed to fetch user data. Please try again later.";
        setError(message);
      }
    };

    fetchData();
  }, [usersLoaded]);

  const filteredUsers = users
    ?.filter((user) => user.accessLevel !== "omni")
    ?.filter((user) => user.uniqueId !== adminId) // Exclude current user
    ?.sort((a, b) => {
      // Sort by accessLevel in descending order
      if (b.accessLevel !== a.accessLevel) {
        return b.accessLevel - a.accessLevel;
      }
      // If accessLevel is the same, sort by lastName alphabetically
      return a.lastName.localeCompare(b.lastName);
    })
    ?.filter(
      // Apply the search filter after sorting
      (user) =>
        user.firstName?.toLowerCase().includes(searchUsers.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchUsers.toLowerCase()) ||
        user.accessLevel?.toString().includes(searchUsers.toLowerCase())
    );

  // console.log(users);
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: "0.5rem",
        }}
      >
        <Box sx={{ fontSize: "1.25rem" }}>{header}</Box>
        <Box sx={{ fontSize: "0.75rem", fontWeight: "400" }}>
          {filteredUsers?.length}
        </Box>
      </Box>
      <Box
        sx={{
          backdropFilter: "blur(1rem)",
          boxShadow: "0 0 1rem rgba(0, 0, 0, 0.2)",
          width: "100%",
          border: `1px solid ${colourTheme.palette.text.primary}`,
          borderRadius: "1rem",
          m: "auto",
          mb: 2.5,
          p: 1,
          height: "auto",
        }}
      >
        {/* Search */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: `1px solid ${colourTheme.palette.text.primary}`,
            padding: "0.125rem 0.25rem",
            borderRadius: "0.5rem",
          }}
        >
          <Box>
            <input
              type="text"
              value={searchUsers}
              onChange={handleSearchChange}
              placeholder="Search Users..."
              style={{
                width: "80%",
                padding: "0.25rem",
                background: "none",
                border: "none",
                outline: "none",
                color: colourTheme.palette.text.primary,
              }}
            />
          </Box>
          <Box sx={{ display: "grid", placeItems: "center" }}>
            <SearchIcon
              style={{
                width: "0.75rem",
                height: "0.75rem",
                cursor: "pointer",
                color: colourTheme.palette.text.primary,
              }}
            />
          </Box>
        </Box>

        {/* List */}
        <Box sx={{ maxHeight: "400px", overflowY: "auto", fontSize: "1rem" }}>
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <User
                key={user.id}
                firstName={user.firstName}
                lastName={user.lastName}
                email={user.email}
                accessLevel={user.accessLevel}
                adminId={adminId}
                userId={user.uniqueId}
              />
            ))
          ) : (
            <Box
              sx={{
                textAlign: "center",
                marginTop: "1rem",
                color: colourTheme.palette.text.secondary,
                fontStyle: "italic",
              }}
            >
              No data available for this enquiry.
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ManageUsers;
